import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { useLocation, Outlet } from '@/router/routerCompat';
import { useTranslation } from 'react-i18next';
import { useApp } from './contexts/useApp';
import { useCookieConsent } from './hooks/useCookieConsent';
import apiService from './services/api';
import { MaintenanceRouteGuard } from './components/MaintenanceRouteGuard';
import { resolveAppShell } from './utils/resolveAppShell';

import { LoadingSpinner } from './components/LoadingSpinner';
import { MessageToast } from './components/MessageToast';
import { ScrollToTop } from './components/ScrollToTop';

// Use named export in lazy chunks to avoid __WEBPACK_DEFAULT_EXPORT__ before init during HMR
const lazyNamed = (importFn, name) => lazy(() => importFn().then(m => ({ default: m[name] ?? m.default })));

const ListenerShell = lazyNamed(() => import('./components/Listener/ListenerShell'), 'ListenerShell');
const AuthShell = lazyNamed(() => import('./components/Auth/AuthShell'), 'AuthShell');
const CreatorShell = lazyNamed(() => import('./components/Creator/CreatorShell'), 'CreatorShell');
const AudioPlayerWaveform = lazyNamed(() => import('./components/Music/AudioPlayerWaveform'), 'AudioPlayerWaveform');
const CookieBanner = lazyNamed(() => import('./components/Common/CookieBanner'), 'CookieBanner');

export function AppLayout() {
  const { state, actions } = useApp();
  const { t } = useTranslation('common');
  const location = useLocation();
  const { consent, loading: consentLoading } = useCookieConsent();
  const appShell = resolveAppShell(location.pathname, {
    isAuthenticated: state.isAuthenticated,
  });
  const isListenerChrome = appShell === 'listener';
  const isAuthShell = appShell === 'auth';
  const isCreatorShell = appShell === 'creator';
  const hidePlayer = appShell === 'minimal';
  const engagementSentRef = useRef(false);

  useEffect(() => {
    if (consentLoading || !consent?.analytics) return () => {};
    if (typeof sessionStorage === 'undefined') return () => {};
    if (sessionStorage.getItem('analytics_engagement_sent') === '1') return () => {};
    let timeoutId;
    const send = () => {
      if (engagementSentRef.current) return;
      engagementSentRef.current = true;
      try {
        sessionStorage.setItem('analytics_engagement_sent', '1');
      } catch (_) { /* ignore quota/private mode */ }
      apiService.sendEngagementBeacon().catch(() => {});
    };
    const onInteraction = () => {
      send();
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener('click', onInteraction, true);
    };
    timeoutId = setTimeout(send, 30000);
    document.addEventListener('click', onInteraction, true);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener('click', onInteraction, true);
    };
  }, [consentLoading, consent?.analytics]);

  useEffect(() => {
    const originalConsoleError = console.error;
    console.error = (...args) => {
      const errorMessage = args.join(' ');
      if (errorMessage.includes('400') && (
          errorMessage.includes('/login') || errorMessage.includes('/register') || errorMessage.includes('/verify-email') ||
          errorMessage.includes('/password-reset') || errorMessage.includes('/accounts/login') || errorMessage.includes('/accounts/register'))) return;
      if (errorMessage.includes('429') || errorMessage.includes('Rate limited') || errorMessage.includes('Too Many Requests')) return;
      if (errorMessage.includes('403') && errorMessage.includes('audio')) {
        console.warn('Audio access denied - this is expected for unauthorized users');
        return;
      }
      if (errorMessage.includes('CORS') && errorMessage.includes('audio')) {
        console.warn('Audio CORS error - this is expected for unauthorized users');
        return;
      }
      if (errorMessage.includes('Failed to fetch') && errorMessage.includes('audio')) {
        console.warn('Audio fetch failed - this is expected for unauthorized users');
        return;
      }
      if (errorMessage.includes('radio-deploy-static.s3.amazonaws.com') && errorMessage.includes('403')) {
        console.warn('S3 audio access denied - this is expected for unauthorized users');
        return;
      }
      if (typeof originalConsoleError === 'function') originalConsoleError.apply(console, args);
    };

    const originalOnError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      const errorMessage = String(message);
      if (errorMessage.includes('403') && errorMessage.includes('audio')) {
        console.warn('Audio access denied - this is expected for unauthorized users');
        return true;
      }
      if (errorMessage.includes('radio-deploy-static.s3.amazonaws.com') && errorMessage.includes('403')) {
        console.warn('S3 audio access denied - this is expected for unauthorized users');
        return true;
      }
      if (errorMessage.includes('403') && (errorMessage.includes('fetch') || errorMessage.includes('load'))) {
        console.warn('Access denied - this is expected for unauthorized users');
        return true;
      }
      if (originalOnError) return originalOnError(message, source, lineno, colno, error);
      return false;
    };

    const originalOnUnhandledRejection = window.onunhandledrejection;
    window.onunhandledrejection = (event) => {
      const errorMessage = String(event.reason);
      if (errorMessage.includes('400') && (
          errorMessage.includes('/login') || errorMessage.includes('/register') || errorMessage.includes('/verify-email') ||
          errorMessage.includes('/password-reset') || errorMessage.includes('/two-factor-auth') || errorMessage.includes('/change-password') ||
          errorMessage.includes('/change-email') || errorMessage.includes('/accounts/login') || errorMessage.includes('/accounts/register') ||
          errorMessage.includes('/accounts/two-factor-auth'))) {
        event.preventDefault();
        return;
      }
      if (errorMessage.includes('429') || errorMessage.includes('Rate limited') || errorMessage.includes('Too Many Requests')) {
        event.preventDefault();
        return;
      }
      if (errorMessage.includes('403') && (errorMessage.includes('audio') || errorMessage.includes('fetch') || errorMessage.includes('load') ||
          errorMessage.includes('radio-deploy-static.s3.amazonaws.com') || (errorMessage.includes('Failed to fetch') && errorMessage.includes('403')))) {
        if (!errorMessage.includes('radio-deploy') && !errorMessage.includes('Failed to fetch')) console.warn('Access denied - this is expected for unauthorized users');
        event.preventDefault();
        return;
      }
      if (originalOnUnhandledRejection) originalOnUnhandledRejection(event);
    };

    const originalFetch = window.fetch;
    if (typeof window.__NATIVE_FETCH__ === 'undefined') {
      window.__NATIVE_FETCH__ = originalFetch;
    }
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        if (response.status === 429) return response;
        if (response.status === 403 && args[0] && typeof args[0] === 'string' && args[0].includes('audio/')) {
          console.warn('Audio access denied - this is expected for unauthorized users');
          return response;
        }
        return response;
      } catch (error) {
        if (error.message && args[0] && typeof args[0] === 'string' && args[0].includes('audio/')) {
          console.warn('Audio loading failed - this is expected for unauthorized users');
          throw error;
        }
        throw error;
      }
    };

    return () => {
      window.fetch = originalFetch;
      if (typeof originalConsoleError === 'function') console.error = originalConsoleError;
      window.onerror = originalOnError;
      window.onunhandledrejection = originalOnUnhandledRejection;
    };
  }, []);

  return (
    <>
      <MaintenanceRouteGuard />
    <div className="min-h-screen bg-[var(--listener-bg,var(--bg-primary))] text-text-primary transition-colors duration-300">
      {!state.authReady && (
        <LoadingSpinner message={t('messages.loading')} />
      )}
      {state.authReady && (
        <>
      {state.backendUnavailable && (
        <div className="sticky top-0 z-[100] flex items-center justify-between gap-4 bg-warning/90 text-warning-content px-4 py-3 text-sm shadow-md">
          <span>{t('backendUnavailable')}</span>
          <button
            type="button"
            onClick={() => actions.retryConnection?.()}
            className="listener-btn listener-btn-ghost shrink-0 !px-3 !py-1.5 text-sm font-medium"
          >
            {t('tryAgain')}
          </button>
        </div>
      )}
      {state.isLoading && <LoadingSpinner message={t('messages.loading')} />}
      {isListenerChrome ? (
        <Suspense fallback={<div className="listener-shell" aria-hidden="true" />}>
          <ListenerShell onLogout={() => actions.logout()}>
            <ScrollToTop />
            <Outlet />
          </ListenerShell>
        </Suspense>
      ) : isCreatorShell ? (
        <Suspense fallback={<div className="listener-shell creator-shell" aria-hidden="true" />}>
          <CreatorShell onLogout={() => actions.logout()}>
            <ScrollToTop />
            <Outlet />
          </CreatorShell>
        </Suspense>
      ) : isAuthShell ? (
        <Suspense fallback={<div className="auth-shell" aria-hidden="true" />}>
          <AuthShell>
            <ScrollToTop />
            <Outlet />
          </AuthShell>
        </Suspense>
      ) : (
        <main className="relative">
          <ScrollToTop />
          <Suspense fallback={<div className="page"><div className="page__content flex items-center justify-center min-h-[200px] text-text-secondary">{t('messages.loading')}</div></div>}>
            <Outlet />
          </Suspense>
        </main>
      )}
      <Suspense fallback={null}>
        {!hidePlayer ? <AudioPlayerWaveform /> : null}
      </Suspense>
      <Suspense fallback={null}>
        <CookieBanner />
      </Suspense>
      {state.error && <MessageToast message={state.error} type="error" onClose={() => actions.clearMessages()} />}
      {state.success && <MessageToast message={state.success} type="success" onClose={() => actions.clearMessages()} />}
      {state.warning && <MessageToast message={state.warning} type="warning" onClose={() => actions.clearMessages()} />}
      {state.info && <MessageToast message={state.info} type="info" onClose={() => actions.clearMessages()} />}
        </>
      )}
    </div>
    </>
  );
}
